import React, { useEffect, useState } from "react";
import {
  FaBuilding,
  FaShieldAlt,
  FaUsers,
  FaKey,
  FaLock,
  FaBalanceScale,
  FaChevronRight,
  FaTrash,
  FaExclamationTriangle,
} from "react-icons/fa";

import { colors, shadows, radius, spacing, typography, transitions, cardBase, pageContainer } from "../../utils/designTokens";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";

const HomeSuperAdmin = ({ setSelected }) => {
  // F2 — Churn scores de la flota
  const [churnList, setChurnList] = useState([]);
  const [loadingChurn, setLoadingChurn] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setLoadingChurn(true);
        const headers = await getAuthHeaders();
        const resp = await fetch(`${HABI_API_BASE_URL}/api/churn-scores-flota`, { headers });
        if (!resp.ok) return;
        const data = await resp.json();
        setChurnList(data.items || []);
      } catch (e) {
        console.warn("[F2] no se pudieron cargar churn scores:", e);
      } finally {
        setLoadingChurn(false);
      }
    })();
  }, []);

  const redZone = churnList.filter((c) => c.zone === "rojo");
  const yellowZone = churnList.filter((c) => c.zone === "amarillo");

  const menuItems = [
    {
      id: "edificios",
      icon: FaBuilding,
      title: "Edificios",
      description: "Accedé a la lista completa de edificios registrados.",
    },
    {
      id: "gestionadmins",
      icon: FaUsers,
      title: "Administradores",
      description: "Agregá, editá y eliminá administradores del sistema.",
    },
    {
      id: "permisosadmin",
      icon: FaKey,
      title: "Permisos por Admin",
      description: "Configurá los accesos y permisos de cada administrador.",
    },
    {
      id: "permisosusuario",
      icon: FaLock,
      title: "Permisos por Usuario",
      description: "Definí qué funciones puede realizar cada usuario.",
    },
    {
      id: "criteriosdivisiongastos",
      icon: FaBalanceScale,
      title: "Criterios de división de gastos",
      description: "Configurá los criterios para dividir gastos entre unidades.",
    },
    {
      id: "solicitudeseliminacion",
      icon: FaTrash,
      title: "Solicitudes de Eliminación",
      description: "Revisá y aprobá o rechazá solicitudes de eliminación de edificios.",
    },
    {
      id: "kbhabitia",
      icon: FaKey,  // reusamos icono — no hay FaBook importado
      title: "Knowledge Base interna",
      description: "Resoluciones notables, decisiones, casos legales. Buscable por staff.",
    },
  ];

  return (
    <div style={styles.container}>
      {/* Encabezado */}
      <div style={styles.headerCard}>
        <div style={styles.badge}>
          <FaShieldAlt size={12} />
          <span>SuperAdmin</span>
        </div>
        <h1 style={styles.title}>Panel de Administración General</h1>
        <p style={styles.subtitle}>
          Gestioná toda la plataforma desde un solo lugar
        </p>
      </div>

      {/* F2 — Widget de churn (edificios en riesgo) */}
      {(redZone.length > 0 || yellowZone.length > 0) && (
        <div style={{
          marginBottom: spacing[6],
          padding: 16,
          background: colors.white,
          borderRadius: 12,
          boxShadow: shadows.sm,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <FaExclamationTriangle size={16} color={redZone.length > 0 ? colors.error : colors.warning} />
            <h3 style={{ margin: 0, fontSize: typography.sizes.lg, fontWeight: 400, color: colors.gray900 }}>
              Salud de la flota
            </h3>
            {redZone.length > 0 && (
              <span style={{
                padding: "2px 10px",
                fontSize: 11,
                borderRadius: 9999,
                background: colors.error,
                color: colors.white,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}>
                {redZone.length} en zona roja
              </span>
            )}
            {yellowZone.length > 0 && (
              <span style={{
                padding: "2px 10px",
                fontSize: 11,
                borderRadius: 9999,
                background: colors.warningLight,
                color: colors.warning,
                textTransform: "uppercase",
                letterSpacing: 0.4,
              }}>
                {yellowZone.length} amarillo
              </span>
            )}
          </div>

          {redZone.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: colors.gray600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
                🚨 Edificios en zona roja (atender ahora)
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {redZone.slice(0, 5).map((c) => (
                  <div key={c.building_id} style={{
                    padding: 10,
                    background: colors.errorLight,
                    borderRadius: 8,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}>
                    <span style={{ fontSize: 13, color: colors.gray900 }}>{c.name}</span>
                    <span style={{ fontSize: 11, color: colors.error, fontWeight: 400 }}>
                      score {c.score}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {yellowZone.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: colors.gray600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.4 }}>
                ⚠ Zona amarilla (vigilar)
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {yellowZone.slice(0, 8).map((c) => (
                  <span key={c.building_id} style={{
                    padding: "4px 12px",
                    background: colors.warningLight,
                    color: colors.warning,
                    borderRadius: 9999,
                    fontSize: 12,
                  }}>
                    {c.name} ({c.score})
                  </span>
                ))}
                {yellowZone.length > 8 && (
                  <span style={{ fontSize: 12, color: colors.gray600 }}>
                    +{yellowZone.length - 8} más
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contenido */}
      <div style={styles.cardGrid}>
        {menuItems.map((item) => (
          <div
            key={item.id}
            style={styles.card}
            onClick={() => setSelected(item.id)}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = shadows.md;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = shadows.sm;
            }}
          >
            <div style={styles.cardHeader}>
              <div style={styles.iconBox}>
                <item.icon size={20} color={colors.primary} />
              </div>
              <FaChevronRight size={14} color={colors.gray400} />
            </div>
            <div style={styles.cardContent}>
              <h3 style={styles.cardTitle}>{item.title}</h3>
              <p style={styles.cardText}>{item.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  // Contenedor principal
  container: {
    ...pageContainer,
  },
  // Tarjeta de encabezado con badge SuperAdmin
  headerCard: {
    ...cardBase,
    padding: "24px 28px",
    marginBottom: spacing[6],
  },
  // Badge de rol SuperAdmin
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: "5px 12px",
    borderRadius: 6,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    marginBottom: spacing[3],
  },
  title: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    margin: 0,
    color: colors.primary,
    display: "flex",
    alignItems: "center",
  },
  subtitle: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    marginTop: 6,
    marginBottom: 0,
  },
  // Grilla de tarjetas de menú
  cardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: spacing[4],
  },
  // Tarjeta de opción de menú
  card: {
    ...cardBase,
    cursor: "pointer",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
  },
  // Contenedor de ícono
  iconBox: {
    width: 44,
    height: 44,
    borderRadius: radius.md,
    backgroundColor: colors.primarySubtle,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  cardContent: {
    flex: 1,
  },
  cardTitle: {
    margin: 0,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.black,
  },
  cardText: {
    margin: "6px 0 0",
    fontSize: typography.sizes.base,
    color: colors.gray600,
    lineHeight: 1.4,
  },
};

export default HomeSuperAdmin;
